import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AvailableServiceLinks } from "./Components/AvailableServiceLinks";
import { LinksMenu } from "./Components/LinksMenu";
import { VisibleServiceLinks } from "./Components/VisibleServiceLinks";
import { UserInfo } from "../../../../services/api/methodsTypes";
import { TPopoverIds } from "../../../../app/state/popoverSlice/types";
import { useAppSelector } from "../../../../services/redux/hooks/use-selector";
import { useMenu } from "../../../hooks/use-menu";
import { useHeaderNav } from "../../hooks/use-header-nav";
import { selectVisibleLinks } from "../../redux/selectors";
import { ServiceLink } from "../../types";
import { getFilteredLinks } from "../../helpers";
import { selectUserInfo } from "../../../../features/Auth/redux/selectors";
import { usePopoverSelectors } from "../../../../app/state/popoverSlice/hook/use-popover-selectors";
import styles from "./styles.module.css";

interface Props {
  closePopover: (id: TPopoverIds) => void;
}

export const HeaderServiceManager = ({ closePopover }: Props) => {
  const userInfo = useAppSelector(selectUserInfo);
  const headerServiceManager = usePopoverSelectors(
    "header_service_manager_popover",
  );
  const { anchorEl, open, handleMenu, handleMenuClose } = useMenu();
  const {
    handleChangeVisibleLinks,
    handleDragStart,
    handleDeleteLink,
    handleDragOver,
    handleDrop,
  } = useHeaderNav();
  const navigate = useNavigate();
  const links = useAppSelector(selectVisibleLinks);
  const [selectedPath, setSelectedPath] = useState<ServiceLink | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isPositionCalculated, setIsPositionCalculated] = useState(false);

  const availableLinks = getFilteredLinks(
    (userInfo as UserInfo)?.["user-groups"],
    links,
  );

  useEffect(() => {
    if (!headerServiceManager.isOpen) return;

    const handleWindowBlur = () => {
      setTimeout(() => {
        if (document.activeElement?.tagName === "IFRAME") {
          closePopover("header_service_manager_popover");
        }
      }, 0);
    };

    window.addEventListener("blur", handleWindowBlur);
    return () => window.removeEventListener("blur", handleWindowBlur);
  }, [headerServiceManager.isOpen, closePopover]);

  const calculatePosition = useCallback(() => {
    if (headerServiceManager.position) {
      const { x, y } = headerServiceManager.position;
      setPosition({
        top: y + 35,
        left: x,
      });
    }
    setIsPositionCalculated(true);
  }, [headerServiceManager.position]);

  useEffect(() => {
    if (headerServiceManager.isOpen && headerServiceManager.position) {
      calculatePosition();
    } else {
      setIsPositionCalculated(false);
    }
  }, [
    headerServiceManager.isOpen,
    headerServiceManager.position,
    calculatePosition,
  ]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closePopover("header_service_manager_popover");
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      const popoverElement = document.querySelector(
        `.${styles["navigation-popover"]}`,
      );
      if (popoverElement && !popoverElement.contains(event.target as Node)) {
        closePopover("header_service_manager_popover");
      }
    };

    if (headerServiceManager.isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [headerServiceManager.isOpen, closePopover]);

  useLayoutEffect(() => {
    if (headerServiceManager.isOpen) {
      const handleResize = () => {
        requestAnimationFrame(calculatePosition);
      };

      window.addEventListener("resize", handleResize, { passive: true });
      return () => window.removeEventListener("resize", handleResize);
    }
  }, [headerServiceManager.isOpen, calculatePosition]);

  if (!headerServiceManager.isOpen) return null;

  const handleTargetLink = (e: any) => {
    if (selectedPath) {
      navigate(selectedPath);
    }
    closePopover("header_service_manager_popover");
    handleMenuClose();
  };

  const handleMakeLinkVisible = () => {
    if (selectedPath) {
      handleChangeVisibleLinks(selectedPath);
    }
  };

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    link: string,
    index: number,
  ) => {
    if ((Object.values(availableLinks) as string[]).includes(link)) {
      setActiveIndex(index);
      setSelectedPath(link as ServiceLink);
      handleMenu(event);
    }
  };
  return (
    <>
      {isPositionCalculated && (
        <div
          className={styles["navigation-popover"]}
          style={{
            position: "fixed",
            top: position.top,
            left: position.left,
            zIndex: 1300,
          }}
        >
          <div className={styles["content"]}>
            <VisibleServiceLinks
              visibleLinks={links}
              onDeleteVisibleLink={handleDeleteLink}
              handleDragOver={handleDragOver}
              handleDragStart={handleDragStart}
              handleDrop={handleDrop}
            />
            <AvailableServiceLinks
              activeIndex={activeIndex}
              availableLinks={availableLinks.filter(
                (link) => link !== "/artifacts",
              )}
              handleOpenMenu={handleOpenMenu}
              handleCloseMenu={handleMenuClose}
              children={
                <LinksMenu
                  activeMenu={open}
                  anchorEl={anchorEl}
                  disabledAddVisibleLink={links?.length === 6}
                  handleTargetLink={handleTargetLink}
                  handleMakeLinkVisible={handleMakeLinkVisible}
                />
              }
            />
          </div>
        </div>
      )}
    </>
  );
};
